import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useEffect, useMemo } from 'react';
import { Switch } from '@mui/material';
import { BuildRounded, ViewCarouselRounded, ViewQuiltRounded } from '@mui/icons-material';
import { useMounted, usePartial } from 'shared/hooks/utils';
import { select_ui_settings, set_device_service_mode, set_ui_settings, } from 'console/redux/settings-actions';
import { UniversalMenu } from 'console/shared/components/menu';
import { useAction, useAppSelector } from 'shared/hooks/redux/hooks';
import { PanelsKeys } from 'console/shared/content/panels/types';
import { ServicePasswordPrompt } from 'z3k-settings/layout/service-password-prompt';
import { is_dashboard_selected } from 'console/dashboards/z3k/selectors';
import { useDeviceState } from 'console/shared/contexts/device/state';
import { useDeviceUISettings } from 'console/shared/contexts/device-ui-settings';
import { useDeviceConfig } from 'console/shared/contexts/device/config';
import { setServiceModeManual } from 'console/redux/devices/items/actions';
import { selectDevice } from 'entities/device/lib';
import { useBooleanState } from 'shared/hooks/state/boolean';
const useSetServiceView = () => {
    const { device_id, z3k_config, device_spec, support_service_mode, is_legacy_device } = useDeviceConfig();
    const { is_service_mode } = useDeviceState();
    const is_mounted = useMounted();
    const [local_service_mode, setServiceMode, noServiceMode] = useBooleanState(is_service_mode);
    const manual = useAppSelector((state) => selectDevice(state, device_id))?.service_mode_manual;
    const setManual = useAction(setServiceModeManual);
    useEffect(() => {
        if (is_mounted) {
            if (is_service_mode != local_service_mode) {
                if (is_service_mode) {
                    setServiceMode();
                }
                else {
                    noServiceMode();
                }
            }
        }
    }, [is_service_mode]);
    const setDeviceServiceMode = useAction(set_device_service_mode);
    const onToggleLegacyServiceMode = useCallback(() => {
        if (!manual) {
            setManual(device_id);
        }
        setDeviceServiceMode(device_id, !local_service_mode);
    }, [local_service_mode, device_id, manual]);
    const toggleZ3KServiceMode = useCallback(() => {
        if (!manual) {
            setManual(device_id);
        }
        setDeviceServiceMode(device_id, !local_service_mode);
    }, [local_service_mode, device_id, manual]);
    const usb_password = useMemo(() => z3k_config?.usbpassword, [z3k_config?.usbpassword]);
    const servicePassword = useMemo(() => usb_password ?? '', [usb_password]);
    const [servicePasswordOpen, closeServicePassword, openServicePassword] = useBooleanState(false);
    const default_password = useMemo(() => device_spec != null
        ? device_spec.io.has_graph_panel_climatic
            ? '0000'
            : 'admin'
        : 'admin', [device_spec]);
    const onEnterServiceView = usePartial(toggleZ3KServiceMode, true);
    const onLeaveServiceView = usePartial(toggleZ3KServiceMode, false);
    const onServiceModeClick = useCallback(() => {
        if (is_service_mode) {
            onLeaveServiceView();
        }
        else {
            openServicePassword();
        }
    }, [is_service_mode, onLeaveServiceView, openServicePassword]);
    if (is_legacy_device) {
        return [onToggleLegacyServiceMode, null];
    }
    return [
        onServiceModeClick,
        support_service_mode ? (_jsx(ServicePasswordPrompt, { open: servicePasswordOpen, serviceView: is_service_mode, actualPassword: servicePassword, defaultPassword: default_password, onEnterServiceMode: onEnterServiceView, onClose: closeServicePassword })) : null,
    ];
};
export const UIControlMenu = memo(function ControlModeMenu(props) {
    const { onClose, anchor } = props;
    const { sorting_mode, panels, simple_view_mode: tab_simple_view_mode, dashboard_sorting_mode, } = useDeviceUISettings();
    const { is_service_mode } = useDeviceState();
    const dashboard_selected = useAppSelector(is_dashboard_selected);
    const show_simple_view_switcher = useMemo(() => {
        const has_panels = !!panels?.find((panel) => panel.id === PanelsKeys.heating_circuits ||
            panel.id === PanelsKeys.simple_view);
        return has_panels && !dashboard_selected;
    }, [panels, dashboard_selected]);
    const [onToggleServiceMode, service_mode_dialog] = useSetServiceView();
    const { device_id, support_service_mode, tab_key } = useDeviceConfig();
    const setUISettings = useAction(set_ui_settings);
    const onSortingModeClick = useCallback(() => {
        setUISettings({
            sorting_mode: sorting_mode ? false : device_id,
            grouping_mode: false,
        });
    }, [setUISettings, sorting_mode, device_id]);
    const ui_settings = useAppSelector(select_ui_settings);
    const { simple_view_mode } = ui_settings;
    const onEnableSimpleViewMode = useCallback(() => {
        setUISettings({
            simple_view_mode: {
                [device_id]: {
                    [tab_key]: {
                        enabled: !tab_simple_view_mode,
                    },
                },
            },
        });
    }, [tab_key, simple_view_mode, tab_simple_view_mode, device_id]);
    const menu_items = useMemo(() => [
        {
            icon: (_jsx(BuildRounded, { fontSize: "small", color: is_service_mode ? 'warning' : 'action' })),
            label: `Сервисный режим`,
            data_tooltip: 'modal-service-view',
            onClick: onToggleServiceMode,
            additional_view: (_jsx(Switch, { size: "small", color: "warning", checked: is_service_mode })),
            show: support_service_mode,
        },
        {
            icon: (_jsx(ViewQuiltRounded, { fontSize: "small", color: sorting_mode ? 'primary' : 'action' })),
            label: `Режим конструктора`,
            data_tooltip: 'modal-constructor-mode',
            disabled: tab_simple_view_mode || dashboard_sorting_mode,
            onClick: onSortingModeClick,
            additional_view: _jsx(Switch, { size: "small", checked: sorting_mode }),
        },
        {
            icon: (_jsx(ViewCarouselRounded, { fontSize: "small", color: tab_simple_view_mode ? 'primary' : 'action' })),
            label: `Упрощенный вид`,
            data_tooltip: 'modal-simplified-view',
            onClick: onEnableSimpleViewMode,
            disabled: sorting_mode,
            additional_view: (_jsx(Switch, { size: "small", color: "primary", checked: tab_simple_view_mode ?? false })),
            show: show_simple_view_switcher,
        },
    ], [
        is_service_mode,
        sorting_mode,
        tab_simple_view_mode,
        show_simple_view_switcher,
        dashboard_sorting_mode,
        onToggleServiceMode,
    ]);
    return (_jsxs(_Fragment, { children: [_jsx(UniversalMenu, { anchor: anchor, menu_items: menu_items, onClose: onClose, opened: !!anchor }), service_mode_dialog] }));
});
